import React, { useEffect, useState } from 'react';
import { getManualReviews } from '../services/api';
import { auth } from '../services/auth';
import api from '../services/api';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import styles from './Alerts.module.scss';

interface Review {
  id: string;
  author: string;
  content: string;
  rating: number;
  date?: string;
  seen?: boolean;
}

const Alerts: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [smsAlerts, setSmsAlerts] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Utilisateur non connecté');

        // Fetch reviews
        const allReviews = await getManualReviews();
setReviews(allReviews.map((r: any) => ({ ...r, id: r.id ?? r._id })));

        // Fetch preferences
        const db = getFirestore();
        const prefRef = doc(db, 'alertPreferences', user.uid);
        const snap = await getDoc(prefRef);
        if (snap.exists()) {
          const prefs = snap.data();
          setEmailAlerts(prefs.emailAlerts ?? true);
          setSmsAlerts(prefs.smsAlerts ?? false);
        }
      } catch (err) {
        console.error(err);
        setError("Erreur lors du chargement des alertes.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const updatePreferences = async (updates: Partial<{ emailAlerts: boolean; smsAlerts: boolean }>) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const db = getFirestore();
      const ref = doc(db, 'alertPreferences', user.uid);
      await setDoc(
        ref,
        {
          firebaseUid: user.uid,
          emailAlerts,
          smsAlerts,
          ...updates,
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Erreur lors de la mise à jour des préférences :', err);
    }
  };

  const handleMarkSeen = async (id: string, seen: boolean) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const idToken = await user.getIdToken();
      await api.put(`/reviews/${id}/seen`, { seen }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      setReviews(prev =>
        prev.map(r => (r.id === id ? { ...r, seen } : r))
      );
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut vu :', err);
    }
  };

  if (loading) return <div>Chargement des alertes...</div>;
  if (error) return <div>{error}</div>;

// Hide alerts that were marked as seen
const negativeReviews = reviews.filter((r) => r.rating <= 2 && !r.seen);

  return (
    <main className={styles.alertsContainer}>
      <h1>Alertes</h1>

      <div style={{ marginBottom: 20 }}>
        <label style={{ marginRight: 16 }}>
          <input
            type="checkbox"
            checked={emailAlerts}
            onChange={e => {
              const val = e.target.checked;
              setEmailAlerts(val);
              updatePreferences({ emailAlerts: val });
            }}
          />
          &nbsp;Recevoir des alertes par email
        </label>
     
        {emailAlerts && <div style={{ color: '#388e3c', marginTop: 6 }}>Notifications email activées</div>}
        {smsAlerts && <div style={{ color: '#388e3c', marginTop: 6 }}>Notifications WhatsApp/SMS activées</div>}
      </div>

      {negativeReviews.length === 0 ? (
        <p>Aucune alerte pour le moment. Aucun avis négatif détecté.</p>
      ) : (
        <div className={styles.alertsList}>
          {negativeReviews.map((review) => (
            <div
              key={review.id}
              className={`${styles.alertCard} ${review.seen ? styles.seenAlertCard : ''}`}
            >
              <div className={styles.alertCardTitle}>⚠️ Avis négatif</div>
              <div className={styles.alertCardHeader}>
                <strong className={styles.alertCardAuthor}>{review.author}</strong>
                <span className={styles.alertCardRating}>({review.rating}/5)</span>
              </div>
              <div className={styles.alertCardContent}>{review.content}</div>
              {review.date && <div className={styles.alertCardDate}>{review.date}</div>}
              <div style={{ marginTop: 10 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={review.seen || false}
                    onChange={e => handleMarkSeen(review.id, e.target.checked)}
                  />
                  &nbsp;Alerte traitée
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default Alerts;
